import type { UploadPartUrl } from '@/types/api'

/** Max part numbers requested per `POST /media/uploads/{id}/parts` (the intent returns the first ≤ 20). */
export const PART_URL_BATCH = 20
/** A presigned URL this close to expiry is refreshed before use (each URL lives ~15 min). */
export const URL_EXPIRY_MARGIN_MS = 60_000

/** Byte range `[start, end)` of a 1-based part. */
export function partRange(partNumber: number, partSize: number, fileSize: number): [number, number] {
  const start = (partNumber - 1) * partSize
  return [Math.min(start, fileSize), Math.min(start + partSize, fileSize)]
}

export function partLength(partNumber: number, partSize: number, fileSize: number): number {
  const [a, b] = partRange(partNumber, partSize, fileSize)
  return b - a
}

export function isUrlUsable(part: Pick<UploadPartUrl, 'expires_at'> | undefined, now: number, marginMs = URL_EXPIRY_MARGIN_MS): boolean {
  if (!part) return false
  const exp = Date.parse(part.expires_at)
  // Unparseable expiry: trust the URL; storage will 403 and the retry path refreshes it.
  if (Number.isNaN(exp)) return true
  return exp - now > marginMs
}

/**
 * Part numbers to request fresh URLs for, starting at `from` (the part about to be
 * uploaded) so the next ones are ready too. Skips parts that are done or in flight
 * and parts whose URL is still usable; wraps around to earlier parts (retries) last.
 */
export function partNumbersToRequest({
  partCount,
  known,
  skip,
  now,
  from = 1,
  batch = PART_URL_BATCH,
  marginMs = URL_EXPIRY_MARGIN_MS,
}: {
  partCount: number
  known: ReadonlyMap<number, UploadPartUrl>
  /** Done or in-flight parts. */
  skip: ReadonlySet<number>
  now: number
  from?: number
  batch?: number
  marginMs?: number
}): number[] {
  const out: number[] = []
  const start = Math.min(Math.max(1, from), Math.max(1, partCount))
  for (let i = 0; i < partCount && out.length < batch; i++) {
    const n = ((start - 1 + i) % partCount) + 1
    if (skip.has(n)) continue
    if (isUrlUsable(known.get(n), now, marginMs)) continue
    out.push(n)
  }
  return out
}

/** Exponential backoff for part retries: 1 s, 2 s, 4 s … capped at 15 s. */
export function retryDelay(attempt: number, baseMs = 1000): number {
  return Math.min(15_000, baseMs * 2 ** Math.max(0, attempt - 1))
}
